/**
 * Solana Explorer URL Utilities
 *
 * Functions to generate Solana Explorer links for transactions, accounts, etc.
 */

export type SolanaCluster = 'mainnet-beta' | 'devnet' | 'testnet';

/**
 * Get the Solana Explorer URL for a transaction
 * @param signature - Transaction signature
 * @param cluster - Solana cluster (mainnet-beta, devnet, testnet)
 * @returns Full URL to view transaction on Solana Explorer
 */
export function getTransactionExplorerUrl(
  signature: string,
  cluster: SolanaCluster = 'devnet'
): string {
  const baseUrl = 'https://explorer.solana.com/tx';

  if (cluster === 'mainnet-beta') {
    return `${baseUrl}/${signature}`;
  }

  return `${baseUrl}/${signature}?cluster=${cluster}`;
}

/**
 * Get the Solana Explorer URL for an account/address
 * @param address - Account public key
 * @param cluster - Solana cluster (mainnet-beta, devnet, testnet)
 * @returns Full URL to view account on Solana Explorer
 */
export function getAccountExplorerUrl(
  address: string,
  cluster: SolanaCluster = 'devnet'
): string {
  const baseUrl = 'https://explorer.solana.com/address';

  if (cluster === 'mainnet-beta') {
    return `${baseUrl}/${address}`;
  }

  return `${baseUrl}/${address}?cluster=${cluster}`;
}

/**
 * Truncate a transaction signature or address for display
 * @param signature - Full signature or address
 * @param startChars - Number of characters to show at start (default: 8)
 * @param endChars - Number of characters to show at end (default: 8)
 * @returns Truncated string with ellipsis
 */
export function truncateSignature(
  signature: string,
  startChars: number = 8,
  endChars: number = 8
): string {
  if (signature.length <= startChars + endChars) {
    return signature;
  }

  return `${signature.slice(0, startChars)}...${signature.slice(-endChars)}`;
}
